import axios from "axios";
import StatisticLabelOperationsDTO from "@/models/StatisticLabelOperationsDTO";
import StatisticLabelDTO from "@/models/StatisticLabelDTO";
import PaginatedResponseDTO from "@/models/PaginatedResponseDTO";
import PaginationDTO from "@/models/PaginationDTO";
import RequisitesCapacityResponseDTO from "@/models/RequisitesCapacityResponseDTO";
import MerchantUserStatisticsDTO from "@/models/MerchantUserStatisticsDTO";
import MerchantUserBanRequestDTO from "@/models/MerchantUserBanRequestDTO";
import CounterpartyConversionStatisticsDTO from "@/models/CounterpartyConversionStatisticsDTO";
import ReceiptCheckStatisticDTO from "@/models/ReceiptCheckStatisticDTO";
import SupportActivityStatisticDTO from "@/models/SupportActivityStatisticDTO";
import {
    DashboardPlusBlockedTraderDTO,
    DashboardPlusDisputeRateDTO,
    DashboardPlusPayoutDisputesDTO,
    DashboardPlusRejectedDisputesDTO,
    DashboardPlusTeamAcceptanceDTO,
    DashboardPlusWorstConversionDTO,
} from "@/models/DashboardPlusDTO";
import qs from "qs";

const dashboardPlusGet = async <T>(path: string, requestDTO: Record<string, unknown> = {}): Promise<T[]> => {
    const params = {...requestDTO};
    if (params.teamLeadId == null) {
        delete params.teamLeadId;
    }

    const resp = await axios({
        url: `/statistic/dashboard-plus/${path}`,
        method: 'GET',
        params,
        paramsSerializer: query => qs.stringify(query)
    });

    return resp.data as T[];
};

export const statistic = {
    labelsOperations: async (requestDTO: any = {}): Promise<StatisticLabelOperationsDTO[]> => {
        let resp = await axios({
            url: `/statistic/labels-operations`,
            data: {
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as StatisticLabelOperationsDTO[]
    },

    requisitesSummary: async (requestDTO: any = {}): Promise<any> => {
        let resp = await axios({
            url: `/statistic/requisites-summary`,
            data: {
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as any
    },

    traders: async (pagination: PaginationDTO, requestDTO: any = {}): Promise<PaginatedResponseDTO<any>> => {
        let resp = await axios({
            url: `/statistic/traders`,
            data: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as PaginatedResponseDTO<any>
    },

    detailedAmounts: async (requestDTO: any = {}): Promise<any> => {
        let resp = await axios({
            url: `/statistic/detailed/amounts`,
            data: {
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as any
    },

    labels: async (): Promise<StatisticLabelDTO[]> => {
        let resp = await axios({
            url: `/statistic/labels`,
            method: 'GET',
        });

        return resp.data as StatisticLabelDTO[]
    },

    partnerIncomes: async (pagination: PaginationDTO, requestDTO: any = {}): Promise<PaginatedResponseDTO<any>> => {
        let resp = await axios({
            url: `/statistic/partner-incomes`,
            data: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as PaginatedResponseDTO<any>
    },

    requisitesCapacity: async (): Promise<RequisitesCapacityResponseDTO> => {
        let resp = await axios({
            url: `/statistic/requisites-capacity`,
            method: 'GET',
        });

        return resp.data as RequisitesCapacityResponseDTO
    },

    merchantUsers: async (pagination: PaginationDTO, requestDTO: any = {}): Promise<PaginatedResponseDTO<MerchantUserStatisticsDTO>> => {
        let resp = await axios({
            url: `/statistic/merchant-users`,
            data: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as PaginatedResponseDTO<MerchantUserStatisticsDTO>
    },

    counterpartyConversion: async (pagination: PaginationDTO, requestDTO: any = {}): Promise<PaginatedResponseDTO<CounterpartyConversionStatisticsDTO>> => {
        let resp = await axios({
            url: `/statistic/counterparty-conversion`,
            data: {
                limit: pagination.limit,
                offset: pagination.offset,
                ...requestDTO
            },
            headers: {'X-HTTP-METHOD-OVERRIDE': "GET"},
            method: 'POST',
        });

        return resp.data as PaginatedResponseDTO<CounterpartyConversionStatisticsDTO>
    },

    merchantUsersBan: async (body: MerchantUserBanRequestDTO): Promise<void> => {
        await axios({
            url: `/statistic/merchant-users/ban`,
            data: body,
            method: 'POST',
        });
    },

    providerRequestsSummary: async (requestDTO: any = {}): Promise<any[]> => {
        let resp = await axios({
            url: `/statistic/provider-requests-summary`,
            method: 'GET',
            params: {
                ...requestDTO
            },
            paramsSerializer: params => {
                return qs.stringify(params)
            }
        });

        return resp.data as any[]
    },

    receiptChecks: async (requestDTO: { fromDate?: string; toDate?: string } = {}): Promise<ReceiptCheckStatisticDTO[]> => {
        const params: Record<string, string> = {};
        if (requestDTO.fromDate) {
            params.fromDate = requestDTO.fromDate;
        }
        if (requestDTO.toDate) {
            params.toDate = requestDTO.toDate;
        }

        let resp = await axios({
            url: `/statistic/receipt-checks`,
            method: 'GET',
            params,
        });

        return resp.data as ReceiptCheckStatisticDTO[];
    },

    supportActivity: async (requestDTO: any = {}): Promise<SupportActivityStatisticDTO> => {
        let resp = await axios({
            url: `/statistic/support-activity`,
            method: 'GET',
            params: {
                ...requestDTO
            },
            paramsSerializer: params => {
                return qs.stringify(params)
            }
        });

        return resp.data as SupportActivityStatisticDTO
    },

    dashboardPlus: {
        worstConversion: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusWorstConversionDTO[]> => {
            return dashboardPlusGet<DashboardPlusWorstConversionDTO>('worst-conversion', requestDTO);
        },
        rejectedDisputes: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusRejectedDisputesDTO[]> => {
            return dashboardPlusGet<DashboardPlusRejectedDisputesDTO>('rejected-disputes', requestDTO);
        },
        payoutDisputes: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusPayoutDisputesDTO[]> => {
            return dashboardPlusGet<DashboardPlusPayoutDisputesDTO>('payout-disputes', requestDTO);
        },
        teamAcceptance: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusTeamAcceptanceDTO[]> => {
            return dashboardPlusGet<DashboardPlusTeamAcceptanceDTO>('team-acceptance', requestDTO);
        },
        disputeRate: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusDisputeRateDTO[]> => {
            return dashboardPlusGet<DashboardPlusDisputeRateDTO>('dispute-rate', requestDTO);
        },
        blockedTraders: (requestDTO: Record<string, unknown> = {}): Promise<DashboardPlusBlockedTraderDTO[]> => {
            return dashboardPlusGet<DashboardPlusBlockedTraderDTO>('blocked-traders', requestDTO);
        },
    },

}
